import { type JSX } from "react";
import { OfferEditableWrap } from "@/components/ClientOfferView";
import { OFFER_VALIDITY_DAYS } from "@/lib/offerDocumentDefaults";
import styles from "@/components/ClientOfferView.module.css";
import { buildOfferNumber, formatPlDate } from "@/components/offer/offerDocumentUtils";
import type { OfferSectionProps } from "@/components/offer/offerDocumentTypes";

export function OfferHeaderSection({ offerData }: OfferSectionProps): JSX.Element {
  const {
    offerTexts,
    offerDate,
    draftId,
    clientName,
    clientAddress,
    clientPhone,
    clientEmail,
    companyDataLoading = false,
    companyName,
    companyNip,
    companyAddress,
    companyPhone,
    companyEmail,
    companyLogoUrl,
    onOfferTextsChange,
  } = offerData;

  const dateStr = formatPlDate(offerDate);
  const validTo = new Date(offerDate);
  validTo.setDate(validTo.getDate() + OFFER_VALIDITY_DAYS);
  const validUntilStr = formatPlDate(validTo);
  const offerNo = offerData.offerNumber?.trim() || buildOfferNumber(draftId, offerDate);

  const companyNameTrimmed = companyName?.trim() ?? "";
  const companyNipTrimmed = companyNip?.trim() ?? "";
  const companyAddressTrimmed = companyAddress?.trim() ?? "";
  const companyPhoneTrimmed = companyPhone?.trim() ?? "";
  const companyEmailTrimmed = companyEmail?.trim() ?? "";
  const hasAnyCompanyData = Boolean(
    companyNameTrimmed ||
      companyNipTrimmed ||
      companyAddressTrimmed ||
      companyPhoneTrimmed ||
      companyEmailTrimmed,
  );

  const clientNameTrimmed = clientName?.trim() ?? "";
  const clientAddressTrimmed = clientAddress?.trim() ?? "";
  const clientPhoneTrimmed = clientPhone?.trim() ?? "";
  const clientEmailTrimmed = clientEmail?.trim() ?? "";
  const hasAnyClientData = Boolean(
    clientNameTrimmed || clientAddressTrimmed || clientPhoneTrimmed || clientEmailTrimmed,
  );

  const brandName = companyNameTrimmed || "Wykonawca";
  const docTitle = offerTexts?.title?.trim() || "Oferta remontowa";
  const lede = offerTexts?.includesIntro?.trim() ?? "";
  const showLogo = Boolean(companyLogoUrl?.trim());
  const showCompanyNameOnly = !showLogo && Boolean(companyNameTrimmed);

  return (
    <>
      <header className={styles.topbar}>
        <div className={styles.brand}>
          {showLogo ? (
            <div className={styles.brandMark}>
              <img src={companyLogoUrl} alt={brandName} />
            </div>
          ) : null}
          <div>
            <div className={styles.brandName}>{showCompanyNameOnly ? companyNameTrimmed : brandName}</div>
          </div>
        </div>
        <div className={styles.docMeta}>
          <div>
            <span className={styles.docMetaLabel}>Dokument</span>{" "}
            <span className={styles.docMetaValue}>Oferta handlowa</span>
          </div>
          <div>
            <span className={styles.docMetaLabel}>Numer</span>{" "}
            <span className={styles.docMetaOchre}>{offerNo}</span>
          </div>
          <div>
            <span className={styles.docMetaLabel}>Data wystawienia</span>{" "}
            <span className={styles.docMetaValue}>{dateStr}</span>
          </div>
          <div>
            <span className={styles.docMetaLabel}>Ważna do</span>{" "}
            <span className={styles.docMetaValue}>{validUntilStr}</span>
          </div>
        </div>
      </header>
      <div className={styles.topbarRule} />

      <section className={styles.titleStrip}>
        {onOfferTextsChange ? (
          <OfferEditableWrap enabled>
            <h1
              className={styles.title}
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) =>
                onOfferTextsChange({
                  title: e.currentTarget.textContent?.trim() ?? "",
                })
              }
            >
              {docTitle}
            </h1>
          </OfferEditableWrap>
        ) : (
          <h1 className={styles.title}>{docTitle}</h1>
        )}
        {onOfferTextsChange ? (
          <OfferEditableWrap enabled>
            <p
              className={styles.lede}
              contentEditable
              suppressContentEditableWarning
              data-placeholder="Kliknij aby dodać opis oferty..."
              onBlur={(e) =>
                onOfferTextsChange({
                  includesIntro: e.currentTarget.textContent?.trim() ?? "",
                })
              }
            >
              {lede}
            </p>
          </OfferEditableWrap>
        ) : lede ? (
          <p className={styles.lede}>{lede}</p>
        ) : null}
      </section>

      <section className={styles.parties}>
        <div className={styles.party}>
          <div className={styles.partyLabel}>Wykonawca</div>
          {companyDataLoading ? (
            <div className={styles.partyName}>Ładowanie danych firmy...</div>
          ) : (
            <>
              <div className={styles.partyName}>{brandName}</div>
              {hasAnyCompanyData ? (
                <div className={styles.partyDetails}>
                  {companyNipTrimmed ? (
                    <div className={styles.partyRow}>
                      <span className={styles.partyK}>NIP</span>
                      <span className={styles.partyV}>{companyNipTrimmed}</span>
                    </div>
                  ) : null}
                  {companyAddressTrimmed ? (
                    <div className={styles.partyRow}>
                      <span className={styles.partyK}>Adres</span>
                      <span className={styles.partyV}>{companyAddressTrimmed}</span>
                    </div>
                  ) : null}
                  {companyPhoneTrimmed ? (
                    <div className={styles.partyRow}>
                      <span className={styles.partyK}>Tel.</span>
                      <span className={styles.partyV}>{companyPhoneTrimmed}</span>
                    </div>
                  ) : null}
                  {companyEmailTrimmed ? (
                    <div className={styles.partyRow}>
                      <span className={styles.partyK}>E-mail</span>
                      <span className={styles.partyV}>{companyEmailTrimmed}</span>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
        <div className={styles.partySep} aria-hidden />
        <div className={styles.party}>
          <div className={styles.partyLabel}>Klient</div>
          {hasAnyClientData ? (
            <>
              {clientNameTrimmed ? <div className={styles.partyName}>{clientNameTrimmed}</div> : null}
              <div className={styles.partyDetails}>
                {clientAddressTrimmed ? (
                  <div className={styles.partyRow}>
                    <span className={styles.partyK}>Adres</span>
                    <span className={styles.partyV}>{clientAddressTrimmed}</span>
                  </div>
                ) : null}
                {clientPhoneTrimmed ? (
                  <div className={styles.partyRow}>
                    <span className={styles.partyK}>Tel.</span>
                    <span className={styles.partyV}>{clientPhoneTrimmed}</span>
                  </div>
                ) : null}
                {clientEmailTrimmed ? (
                  <div className={styles.partyRow}>
                    <span className={styles.partyK}>E-mail</span>
                    <span className={styles.partyV}>{clientEmailTrimmed}</span>
                  </div>
                ) : null}
              </div>
            </>
          ) : (
            <div className={styles.partyName}>-</div>
          )}
        </div>
      </section>
    </>
  );
}
